import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import Checkbox from "antd/es/checkbox";
import Form, { FormProps } from "antd/es/form";
import { useForm, useWatch } from "antd/es/form/Form";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { differenceInMinutes } from "date-fns";
import { useIntl } from "react-intl";

import { OrganizationSetting } from "@mapmycustomers/shared";
import ActivityCompletion from "@mapmycustomers/shared/enum/activity/ActivityCompletion";
import ActivityRemindType from "@mapmycustomers/shared/enum/activity/ActivityRemindType";
import ActivityVisibility from "@mapmycustomers/shared/enum/activity/ActivityVisibility";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import SchemaFieldType from "@mapmycustomers/shared/enum/SchemaFieldType";
import { Activity, Deal, EntityType, Person } from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import Company from "@mapmycustomers/shared/types/entity/Company";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import FormLayout, { LayoutSchemaField } from "@mapmycustomers/shared/types/layout/FormLayout";
import User from "@mapmycustomers/shared/types/User";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useRerenderer from "@mapmycustomers/shared/util/hook/useRerenderer";
import { Alert, FormItem, LoadingSpinner } from "@mapmycustomers/ui";

import useMentionsValidation from "@app/component/activity/utils/useMentionsValidation";
import { useRecurIntervalTypeValueChangeHandler } from "@app/component/createEditEntity/Activity/utils/useRecurIntervalTypeValueChangeHandler";
import FormLayoutSelector from "@app/component/FormLayoutSelector";
import FormNoLayoutAlert from "@app/component/FormNoLayoutAlert";
import EntityModal from "@app/component/modal/EntityModal";
import { isPostCreationModalVisible } from "@app/component/PostCreationModal/store";
import { hideGlobalPostCreationModal } from "@app/component/PostCreationModal/store/actions";
import PreviewModeIndicator from "@app/component/PreviewModeIndicator";
import localSettings from "@app/config/LocalSettings";
import RecurringIntervalType from "@app/enum/RecurringIntervalType";
import CoachingNote from "@app/scene/dashboard/types/CoachingNote";
import { getActivityTypes, getCoachingTaskActivityType } from "@app/store/activity";
import {
  doesOrganizationSupportMultiContactActivities,
  getCurrentUser,
  getOrganizationSettingValue,
  isCurrentUserManager,
  isCurrentUserOwner,
} from "@app/store/iam";
import { getAllTeamsOfEntireOrganization, getUsersOfEntireOrganization } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import FrequencyCriteria from "@app/types/frequncy/FrequencyCriteria";
import HideAction from "@app/types/HideCallback";
import { AnalyticsService } from "@app/util/analytic/AnalyticsService";
import { ACTIVITY_DEFAULT_REMIND_TIME } from "@app/util/consts";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import useFormChangeTracker from "@app/util/hook/form/useFormChangeTracker";
import useActivityVisibilityOptions from "@app/util/hook/useActivityVisibilityOptions";
import useErroredFieldsScroller from "@app/util/hook/useErroredFieldsScroller";
import useFieldValidation from "@app/util/hook/useFieldValidation";
import isValidDate from "@app/util/isValidDate";
import { activityLayoutModel } from "@app/util/layout/impl";

import { activityAnalytics } from "../analytics";
import NotificationSettings from "../NotificationSettings";

import ActivityCompleted from "./component/ActivityCompleted";
import ActivityFooter from "./component/ActivityFooter";
import ActivityFormFields from "./component/ActivityFormFields";
import FrequencyCriteriaField from "./component/FrequencyCriteriaField";
import RelatedEntitiesMatching from "./component/RelatedEntitiesMatching";
import SubmitButton from "./component/SubmitButton";
import styles from "./CreateEditActivityModal.module.scss";
import messages from "./messages";
import {
  getAssociationsState,
  getPrefilledValues,
  getUploadedFilesList,
  isCloningActivity,
  isCreating,
  isInitializing,
} from "./store";
import {
  changeAssociatedEntities,
  clearAllUploadedCreateEditActivityFiles,
  clearAssociations,
  createActivity,
  initializeActivityModal,
  relateUnrelateEntities,
  updateActivityModalAssociations,
} from "./store/actions";
import FormValues from "./types/FormValues";
import { getFollowUpDate } from "./utils/getFollowUpDate";
import getFormInitialValues from "./utils/getFormInitialValues";
import inaccessibleEntityAlertMessage from "./utils/inaccessibleEntityAlertMessage";
import useActivityNameAutoFill from "./utils/useActivityNameAutoFill";
import useCreateCallback from "./utils/useCreateCallback";
import useFrequencyCriteria from "./utils/useFrequencyCriteria";
import { useIsRecurrentValueChangeHandler } from "./utils/useIsRecurrentValueChangeHandler";
import useSaveModeHandler from "./utils/useSaveModeHandler";

const isNotCustomSchemaField = (schemaField: LayoutSchemaField) =>
  schemaField.fieldType !== SchemaFieldType.CUSTOM;

const isNotCustomField = (field: IField) => !field.hasFeature(FieldFeature.CUSTOM_FIELD);

interface CreateActivityModalProps {
  activityTypeExtra?: ReactNode;
  analyticIssuer?: AnalyticsService;
  className?: string;
  coachingNoteId?: CoachingNote["id"];
  defaultActivityTypeId?: ActivityType["id"];
  defaultAssigneeId?: User["id"];
  defaultStartAt?: Date;
  fixedCompany?: Company;
  fixedDeal?: Deal;
  fixedPerson?: Person;
  frequencyCriteria?: FrequencyCriteria;
  lockType?: boolean;
  onHide: HideAction<Activity>;
  onSuccess?: () => void;
  previewLayout?: FormLayout;
  subjectUserId?: User["id"];
}

const CreateActivityModal = ({
  activityTypeExtra,
  activityTypes,
  analyticIssuer,
  associations,
  className,
  clearAllUploadedFiles,
  clearAssociations,
  cloning,
  coachingNoteId,
  coachingTaskTypeId,
  createActivity,
  creating,
  currentUser,
  defaultAssignee,
  fixedCompany,
  fixedDeal,
  fixedPerson,
  frequencyCriteria,
  hideGlobalPostCreationModal,
  initialActivityTypeId,
  initialCompletion,
  initialize,
  initializing,
  initialStartAt,
  initialVisibility,
  isManager,
  isMultiContactEnabled,
  isOwner,
  isPostCreationModalVisible,
  lockType,
  onChangeAssociatedEntities,
  onHide,
  onRelateUnrelateEntities,
  onSuccess,
  onUpdateActivityModalAssociations,
  prefilledValues,
  previewLayout,
  subjectUserId,
  teams,
  uploadedFiles,
  users,
}: CreateActivityModalProps & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const intl = useIntl();
  const forceRender = useRerenderer();

  const [formLayout, setFormLayout] = useState(activityLayoutModel.defaultFormLayout);
  const [formFieldsRef, handleFinishFail] = useErroredFieldsScroller();

  const [shouldCreateAnother, , , toggleCreateAnother] = useBoolean(false);
  const [isActivityNameChangedByUser, setIsActivityNameChangedByUserToTrue] = useBoolean(false);
  const [isFollowUpActivity, setIsFollowUpActivity] = useState(false);

  const handleCreateAnotherChange = useCallback(() => toggleCreateAnother(), [toggleCreateAnother]);
  const visibilityOptions = useActivityVisibilityOptions(isOwner, isManager);

  useEffect(() => {
    initialize({
      assignee: prefilledValues?.assignee,
      fixedCompany: fixedCompany || prefilledValues?.account,
      fixedDeal: fixedDeal || prefilledValues?.deal,
      fixedPerson: fixedPerson || prefilledValues?.contact,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once

  useEffect(() => {
    onUpdateActivityModalAssociations({
      assignee: prefilledValues?.assignee,
      fixedCompany: fixedCompany || prefilledValues?.account,
      fixedDeal: fixedDeal || prefilledValues?.deal,
      fixedPerson: fixedPerson || prefilledValues?.contact,
    });
  }, [fixedCompany, fixedPerson, fixedDeal, prefilledValues, onUpdateActivityModalAssociations]);

  const [form] = useForm<FormValues>();
  const [isFormChanged, handleFormChanges] = useFormChangeTracker(form);

  const handleScheduleFollowUp = useCallback(() => {
    onSuccess?.();
    const { activityTypeId, companyId, dealId, name, peopleIds, personId, startAt } =
      form.getFieldsValue();

    const followUpActivityFields = {
      activityTypeId,
      companyId,
      completed: false,
      dealId,
      name,
      peopleIds,
      personId,
      startAt: startAt ? getFollowUpDate(startAt, localSettings.getFollowUpTime()) : startAt,
    };

    form.resetFields();
    form.setFieldsValue(followUpActivityFields);
    setIsFollowUpActivity(true);
  }, [form, onSuccess]);

  const handleCancelClick = useCallback(() => {
    clearAssociations();
    clearAllUploadedFiles();
    onHide();
  }, [clearAssociations, clearAllUploadedFiles, onHide]);

  const handleActivityModalVisibility: HideAction<Activity> = useCallback(
    (updated?: boolean, activity?: Activity) => {
      onHide(updated, activity);
      if (isPostCreationModalVisible) {
        hideGlobalPostCreationModal();
      }
    },
    [isPostCreationModalVisible, hideGlobalPostCreationModal, onHide]
  );

  const handleFieldValidationErrors = useFieldValidation(form, handleFinishFail, true);

  const handleFormFinish = useCreateCallback(
    activityTypes,
    createActivity,
    form,
    formLayout,
    shouldCreateAnother ? handleScheduleFollowUp : handleActivityModalVisibility,
    analyticIssuer,
    handleFieldValidationErrors
  );

  const [, allowedUserEmails, handleAllMentionedUsersHaveAccessToActivity] = useMentionsValidation(
    currentUser.id,
    users,
    associations.associatedCompany,
    associations.associatedDeal,
    associations.associatedPerson
  );

  const isRecurrentValueChangeHandler = useIsRecurrentValueChangeHandler(form);
  const recurIntervalTypeValueChangeHandler = useRecurIntervalTypeValueChangeHandler(form);
  const doesActivityMeetCriteria = useFrequencyCriteria(
    initialStartAt,
    form,
    initialActivityTypeId,
    frequencyCriteria
  );

  const [{ isActivityPublic }, saveModeHandler] = useSaveModeHandler();

  const handleActivityChange = useCallback(
    (values: FormValues, allValues: FormValues) => {
      if (allValues.endAt && allValues.startAt && allValues.endAt < allValues.startAt) {
        form.setFieldsValue({ endAt: allValues.startAt });
      }
    },
    [form]
  );

  const handleFormChangeRerender = useCallback(() => {
    handleFormChanges();
    forceRender();
  }, [forceRender, handleFormChanges]);

  const [activityNameAutoFillValueChangeHandler] = useActivityNameAutoFill(
    form,
    isActivityNameChangedByUser,
    isFollowUpActivity
  );

  const handleValuesChange = useCallback<NonNullable<FormProps<FormValues>["onValuesChange"]>>(
    (changedValues, values) => {
      [
        activityNameAutoFillValueChangeHandler,
        isRecurrentValueChangeHandler,
        recurIntervalTypeValueChangeHandler,
        saveModeHandler,
        handleFormChanges,
        handleAllMentionedUsersHaveAccessToActivity,
        handleActivityChange,
      ].forEach((fn) => fn(changedValues, values));
    },
    [
      activityNameAutoFillValueChangeHandler,
      handleActivityChange,
      handleAllMentionedUsersHaveAccessToActivity,
      handleFormChanges,
      isRecurrentValueChangeHandler,
      recurIntervalTypeValueChangeHandler,
      saveModeHandler,
    ]
  );

  const isPreviewMode = !!previewLayout;

  const currentLayout = isPreviewMode ? previewLayout : formLayout;

  const submitButtonDisabled = creating || initializing || isPreviewMode || !currentLayout;
  const showConfirm =
    !isPreviewMode && !initializing && (isFormChanged || uploadedFiles.length > 0);

  const isCompleted = form.getFieldValue(ActivityFieldName.COMPLETED);
  const dateValue = form.getFieldValue(ActivityFieldName.START_AT);
  const isPastDate = isValidDate(dateValue) && differenceInMinutes(new Date(), dateValue) >= 60;

  const defaultLayoutActivityTypeId = useMemo(() => {
    return currentLayout?.schema.find((field) => field.field === "crmActivityTypeId")
      ?.defaultValue?.[0] as ActivityType["id"];
  }, [currentLayout]);

  const resolvedActivityTypeId = initialActivityTypeId ?? defaultLayoutActivityTypeId;
  const lockedActivityType = useMemo(
    () => (lockType ? activityTypes.find(({ id }) => id === resolvedActivityTypeId) : undefined),
    [activityTypes, lockType, resolvedActivityTypeId]
  );

  const currentActivityTypeId = useWatch("activityTypeId", form) ?? resolvedActivityTypeId;
  // Coaching tasks are created via AddTaskModal / createCoachingTask, not this modal — and
  // the coaching task type is excluded from getActivityTypes, so this guard is effectively
  // a no-op today. Kept (with lazy getCoachingTaskActivityType) in case a future caller
  // passes a coaching-task lockType or prefilled activityTypeId.
  const isCoachingTaskActivity =
    isDefined(coachingTaskTypeId) && currentActivityTypeId === coachingTaskTypeId;

  const resolvedAssignee = defaultAssignee ?? currentUser;
  const initialValues = prefilledValues
    ? {
        ...getFormInitialValues(
          prefilledValues,
          resolvedAssignee,
          resolvedActivityTypeId,
          initialCompletion,
          initialVisibility,
          true
        ),
        coachingNoteId,
        subjectUserId,
      }
    : {
        activityTypeId: resolvedActivityTypeId,
        assignee: resolvedAssignee,
        assigneeId: resolvedAssignee.id,
        coachingNoteId,
        companyId: fixedCompany?.id,
        completed: initialCompletion,
        dealId: fixedDeal?.id,
        isRecurrent: false,
        name: "",
        peopleIds: isMultiContactEnabled && fixedPerson ? [fixedPerson.id] : undefined,
        personId: !isMultiContactEnabled ? fixedPerson?.id : undefined,
        recurIntervalType: RecurringIntervalType.NEVER,
        reminders: localSettings.getActivityCreationNotifyPreferences() ?? [
          { remindBeforeMinutes: ACTIVITY_DEFAULT_REMIND_TIME, remindVia: ActivityRemindType.PUSH },
        ],
        startAt: initialStartAt,
        subjectUserId,
        teamIds: [],
        visibility: initialVisibility,
      };

  const appendAddableFieldsWithFilter = useCallback(
    (field: IField) => (prefilledValues ? field.hasNonEmptyValueFor(prefilledValues) : false),
    [prefilledValues]
  );

  return (
    <AnalyticsContext.Provider value={activityAnalytics}>
      <EntityModal
        actions={
          initializing || !currentLayout ? undefined : (
            <NotificationSettings form={form} onChange={handleFormChanges} />
          )
        }
        className={cn(styles.modal, className)}
        entityTypeName="Activity"
        footer={null}
        onCancel={handleCancelClick}
        showConfirm={showConfirm}
        title={
          <>
            <span className={styles.title}>
              {intl.formatMessage(isFollowUpActivity ? messages.scheduleFollowUp : messages.header)}
            </span>

            {isPreviewMode ? (
              <PreviewModeIndicator />
            ) : (
              <FormLayoutSelector
                disabled={creating}
                entityType={EntityType.ACTIVITY}
                layout={formLayout}
                onChange={setFormLayout}
              />
            )}
          </>
        }
        titleExtra={
          <div className={styles.extra}>
            <FrequencyCriteriaField
              doesActivityMeetCriteria={doesActivityMeetCriteria}
              frequencyCriteria={frequencyCriteria}
              isCompleted={isCompleted}
            />
            <RelatedEntitiesMatching
              associatedCompany={associations.associatedCompany}
              associatedDeal={associations.associatedDeal}
              associatedPerson={associations.associatedPerson}
            />
          </div>
        }
      >
        {initializing ? (
          <LoadingSpinner />
        ) : currentLayout ? (
          <div className={styles.formContainer} ref={formFieldsRef}>
            <Form<FormValues>
              className={styles.form}
              form={form}
              initialValues={initialValues}
              layout="vertical"
              onFinish={handleFormFinish}
              onFinishFailed={handleFinishFail}
              onValuesChange={handleValuesChange}
              preserve
            >
              <div className={styles.content}>
                {associations.inaccessibleEntity && (
                  <Alert
                    className={styles.inaccessibleEntityAlert}
                    message={intl.formatMessage(inaccessibleEntityAlertMessage)}
                    showIcon
                    type="warning"
                  />
                )}
                {cloning && (
                  <Alert
                    className={styles.inaccessibleEntityAlert}
                    closable
                    message={intl.formatMessage(messages.duplicated)}
                    showIcon
                    type="info"
                  />
                )}
                <ActivityFormFields
                  activityTypeExtra={activityTypeExtra}
                  activityTypes={activityTypes}
                  allowedUserEmails={allowedUserEmails}
                  appendAddableFieldsWithFilter={appendAddableFieldsWithFilter}
                  associations={{
                    associations,
                    fixedCompany,
                    fixedDeal,
                    fixedPerson,
                    isMultiContactEnabled,
                    onChangeAssociatedEntities,
                    onRelateUnrelateEntities,
                  }}
                  canAddField={isCoachingTaskActivity ? isNotCustomField : undefined}
                  coachingTaskTypeId={coachingTaskTypeId}
                  filterFields={isCoachingTaskActivity ? isNotCustomSchemaField : undefined}
                  form={form}
                  ignoreRequired={!isCompleted}
                  layout={currentLayout}
                  lockedActivityType={lockedActivityType}
                  onActivityNameChange={setIsActivityNameChangedByUserToTrue}
                  teams={teams}
                  users={users}
                  visibilityOptions={visibilityOptions}
                >
                  <FormItem hidden name="subjectUserId" />
                  <FormItem hidden name="coachingNoteId" />
                </ActivityFormFields>
              </div>
              <div className={styles.footer}>
                <div className={styles.top}>
                  <div className={styles.left}>
                    <ActivityFooter
                      form={form}
                      hideRecurring={isCoachingTaskActivity}
                      isFollowUp={isFollowUpActivity}
                      onChange={handleFormChangeRerender}
                    />
                  </div>

                  <div className={styles.right}>
                    <Tooltip
                      placement="topRight"
                      title={isPreviewMode ? intl.formatMessage(messages.tooltip) : null}
                      trigger={isPreviewMode ? ["hover"] : []}
                    >
                      <span>
                        <SubmitButton
                          buttonTitle={
                            !isValidDate(dateValue)
                              ? intl.formatMessage(messages.saveActivity)
                              : isPastDate || isCompleted
                                ? intl.formatMessage(messages.logActivity)
                                : intl.formatMessage(messages.scheduleActivity)
                          }
                          disabled={submitButtonDisabled}
                          doesActivityMeetCriteria={doesActivityMeetCriteria}
                          isActivityPublic={isActivityPublic}
                          key="submit"
                          loading={creating}
                          onCreate={form.submit}
                        />
                      </span>
                    </Tooltip>
                  </div>
                </div>
                <div className={styles.bottom}>
                  <ActivityCompleted creating form={form} onChange={handleFormChangeRerender} />
                  <Checkbox
                    checked={shouldCreateAnother}
                    disabled={submitButtonDisabled}
                    key="scheduleFollowUp"
                    onChange={handleCreateAnotherChange}
                  >
                    {intl.formatMessage(
                      isFollowUpActivity
                        ? messages.scheduleAnotherFollowUp
                        : messages.scheduleFollowUpNext
                    )}
                  </Checkbox>
                </div>
              </div>
            </Form>
          </div>
        ) : (
          <div className={styles.content}>
            <FormNoLayoutAlert />
          </div>
        )}
      </EntityModal>
    </AnalyticsContext.Provider>
  );
};

const mapStateToProps = (
  state: RootState,
  {
    defaultActivityTypeId,
    defaultAssigneeId,
    defaultStartAt,
    frequencyCriteria,
  }: CreateActivityModalProps
) => {
  const currentUser = getCurrentUser(state)!;
  const orgUsers = getUsersOfEntireOrganization(state);
  return {
    activityTypes: getActivityTypes(state),
    associations: getAssociationsState(state),
    cloning: isCloningActivity(state),
    coachingTaskTypeId: getCoachingTaskActivityType(state)?.id,
    creating: isCreating(state),
    currentUser,
    defaultAssignee:
      defaultAssigneeId != null ? orgUsers.find(({ id }) => id === defaultAssigneeId) : undefined,
    initialActivityTypeId: defaultActivityTypeId ?? frequencyCriteria?.activityTypesIds?.[0],
    initialCompletion:
      getOrganizationSettingValue(state, OrganizationSetting.ACTIVITY_COMPLETION_STATUS) !==
      ActivityCompletion.INCOMPLETE,
    initializing: isInitializing(state),
    initialStartAt: frequencyCriteria?.dueDate ?? defaultStartAt ?? new Date(),
    initialVisibility: ActivityVisibility.PRIVATE as ActivityVisibility,
    isManager: isCurrentUserManager(state),
    isMultiContactEnabled: doesOrganizationSupportMultiContactActivities(state),
    isOwner: isCurrentUserOwner(state),
    isPostCreationModalVisible: isPostCreationModalVisible(state),
    prefilledValues: getPrefilledValues(state),
    teams: getAllTeamsOfEntireOrganization(state),
    uploadedFiles: getUploadedFilesList(state),
    users: orgUsers,
  };
};

const mapDispatchToProps = {
  clearAllUploadedFiles: clearAllUploadedCreateEditActivityFiles,
  clearAssociations: clearAssociations,
  createActivity: createActivity.request,
  hideGlobalPostCreationModal: hideGlobalPostCreationModal,
  initialize: initializeActivityModal.request,
  onChangeAssociatedEntities: changeAssociatedEntities.request,
  onRelateUnrelateEntities: relateUnrelateEntities.request,
  onUpdateActivityModalAssociations: updateActivityModalAssociations.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateActivityModal);
